import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MOOD } from "@/constants/testIds/app";

const EMOTIONS = [
  { id: "anxious", label: "Anxious" },
  { id: "hopeful", label: "Hopeful" },
  { id: "lonely", label: "Lonely" },
  { id: "restless", label: "Restless" },
  { id: "grieving", label: "Grieving" },
  { id: "tender", label: "Tender" },
  { id: "overwhelmed", label: "Overwhelmed" },
  { id: "content", label: "Content" },
  { id: "curious", label: "Curious" },
  { id: "tired", label: "Tired" },
  { id: "grateful", label: "Grateful" },
  { id: "uncertain", label: "Uncertain" },
];

export default function MoodCheckin() {
  const navigate = useNavigate();
  const [emotions, setEmotions] = useState([]);
  const [intensity, setIntensity] = useState(3);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleEmotion = (id) => {
    setEmotions((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.message("Three is the gentle limit", { description: "Deselect one to add another." });
        return prev;
      }
      return [...prev, id];
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (emotions.length === 0) {
      toast.error("Please choose at least one emotion");
      return;
    }
    setBusy(true);
    try {
      const { data: mood } = await api.post("/mood", { emotions, intensity, note: note || null });
      toast.message("Finding thoughtful picks…", { description: "This may take a few seconds." });
      const { data: rec } = await api.post("/recommend", { source_type: "mood", source_id: mood.id });
      navigate(`/recommendations/${rec.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={MOOD.container} className="max-w-3xl">
      <div className="label-caps mb-4">Mood check-in</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        Name it, softly.
      </h1>
      <p className="mt-5 text-[#2C2520]/70 leading-relaxed">
        Choose up to three words that feel closest. Naming a feeling loosens its grip.
      </p>

      <form onSubmit={onSubmit} className="mt-12 space-y-10">
        <div>
          <div className="label-caps mb-4 flex items-center gap-3">
            <span>Emotion</span>
            <span
              data-testid="mood-selected-count"
              className={
                "text-[0.7rem] tracking-wider px-2 py-0.5 rounded-full " +
                (emotions.length > 0 ? "bg-[#4A5D4E] text-white" : "bg-[#EDE8DB] text-[#2C2520]/70")
              }
            >
              {emotions.length}/3 chosen
            </span>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {EMOTIONS.map((em) => {
              const active = emotions.includes(em.id);
              return (
                <button
                  type="button"
                  key={em.id}
                  data-testid={MOOD.emotionOption(em.id)}
                  onClick={() => toggleEmotion(em.id)}
                  className={
                    "px-4 py-2 rounded-full text-sm transition-colors duration-200 border " +
                    (active
                      ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
                      : "bg-[#F5F2EA] text-[#2C2520] border-[#E8E3D5] hover:bg-[#EDE8DB]")
                  }
                >
                  {em.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="label-caps mb-4">Intensity · {intensity} of 5</div>
          <div className="flex gap-3">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = intensity === n;
              const shade = ["#EDE8DB", "#D7D3B7", "#B3BC98", "#7E9080", "#4A5D4E"][n - 1];
              return (
                <button
                  type="button"
                  key={n}
                  data-testid={MOOD.intensity(n)}
                  onClick={() => setIntensity(n)}
                  style={{ background: active ? shade : "#F5F2EA" }}
                  className={
                    "flex-1 aspect-[3/2] rounded-[12px] border transition-all duration-200 " +
                    (active ? "border-[#4A5D4E] text-white shadow-[0_2px_8px_rgba(44,37,32,0.1)]" : "border-[#E8E3D5] text-[#2C2520]/70 hover:border-[#4A5D4E]/40")
                  }
                >
                  <span className="font-serif-display text-2xl">{n}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="label-caps mb-4">A short note (optional)</div>
          <textarea
            data-testid={MOOD.note}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="A line about what's stirring, if you'd like…"
            className="w-full p-4 rounded-[12px] bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520] focus:outline-none focus:border-[#4A5D4E] focus:bg-white transition-colors"
          />
        </div>

        <button
          data-testid={MOOD.submit}
          type="submit"
          disabled={busy}
          className="px-7 py-3.5 rounded-[12px] bg-[#4A5D4E] text-white hover:bg-[#3D4D40] transition-transform active:scale-95 disabled:opacity-60"
        >
          {busy ? "Gathering picks…" : "Receive recommendations"}
        </button>
      </form>
    </div>
  );
}
